import { cloneElement, isValidElement, useCallback, useEffect, useRef, useState, type ReactElement } from "react";

const LG_BREAKPOINT = 1024; // Tailwind's default `lg` breakpoint
const NARROW_WIDTH = 400;
const WIDE_WIDTH = 450;
const MIN_WIDTH = 360;

const getResponsiveWidth = () => (window.innerWidth >= LG_BREAKPOINT ? WIDE_WIDTH : NARROW_WIDTH);

const SIBLING_RESERVED_WIDTH = 32;

interface ChatSizeControls {
  isMaximized?: boolean;
  onToggleMaximize?: () => void;
  onResetSize?: () => void;
}

interface ResizableChatPanelProps {
  children: ReactElement<ChatSizeControls>;
}

const ResizableChatPanel = ({ children }: ResizableChatPanelProps) => {
  const [width, setWidth] = useState(getResponsiveWidth);
  const [isMaximized, setIsMaximized] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const manuallyResizedRef = useRef(false);

  const getMaxWidth = useCallback(() => {
    const containerWidth = panelRef.current?.parentElement?.clientWidth ?? window.innerWidth;
    return Math.max(MIN_WIDTH, containerWidth - SIBLING_RESERVED_WIDTH);
  }, []);


  useEffect(() => {
    const handleWindowResize = () => {
      if (manuallyResizedRef.current) return;
      setWidth(getResponsiveWidth());
    };
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    manuallyResizedRef.current = true;
    setIsMaximized(false);
    const startX = e.clientX;
    const startWidth = width;
    const maxWidth = getMaxWidth();

    const handlePointerMove = (moveEvent: PointerEvent) => {
      if (!draggingRef.current) return;
      const delta = startX - moveEvent.clientX;
      setWidth(Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth + delta)));
    };
    const handlePointerUp = () => {
      draggingRef.current = false;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  }, [width, getMaxWidth]);

  const handleResetSize = useCallback(() => {
    manuallyResizedRef.current = false;
    setIsMaximized(false);
    setWidth(getResponsiveWidth());
  }, []);

  const handleToggleMaximize = useCallback(() => {
    if (isMaximized) {
      manuallyResizedRef.current = false;
      setWidth(getResponsiveWidth());
      setIsMaximized(false);
    } else {
      manuallyResizedRef.current = true;
      setWidth(getMaxWidth());
      setIsMaximized(true);
    }
  }, [isMaximized, getMaxWidth]);

  const child = isValidElement(children)
    ? cloneElement(children, {
        isMaximized,
        onToggleMaximize: handleToggleMaximize,
        onResetSize: handleResetSize,
      })
    : children;

  return (
    <div ref={panelRef} className="relative border-l bg-background flex flex-col shrink-0 overflow-hidden" style={{ width }}>
      <div
        onPointerDown={handlePointerDown}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize chat panel"
        className="absolute left-0 top-0 h-full w-4 -translate-x-1/2 cursor-col-resize z-20 flex justify-center group"
      >
        <div className="w-1 h-full bg-transparent group-hover:bg-primary/40 group-active:bg-primary/60 transition-colors" />
      </div>
      {child}
    </div>
  );
};

export default ResizableChatPanel;
